import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import { supabase } from './lib/supabase';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import Home from './pages/Home';
import ApplicationForm from './pages/ApplicationForm';
import AboutUs from './pages/AboutUs';
import HowItWorks from './pages/HowItWorks';
import WhyUs from './pages/WhyUs';
import FAQ from './pages/FAQ';
import ContactUs from './pages/ContactUs';
import FraudPrevention from './pages/FraudPrevention';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import PersonalLoans from './pages/PersonalLoans';
import InstallmentLoans from './pages/InstallmentLoans';
import BusinessLoans from './pages/BusinessLoans';
import Disclaimer from './pages/Disclaimer';
import DoNotSell from './pages/DoNotSell';
import Unsubscribe from './pages/Unsubscribe';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import Reports from './pages/Reports';
import Leads from './pages/Leads';
import Settings from './pages/Settings';
import NotFound from './pages/NotFound';

// Protected Route component with enhanced security
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = () => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        console.log('Protected route session check:', !!session);
        setIsAuthenticated(!!session);
      });
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, !!session);
      setIsAuthenticated(!!session);
      
      // If user just signed in, ensure we're on the right page
      if (event === 'SIGNED_IN' && session && window.location.pathname === '/admin/login') {
        navigate('/admin', { replace: true });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (isAuthenticated === null) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
};

// Auth Guard component
const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAuth = () => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        console.log('Auth guard session check:', !!session);
        setIsAuthenticated(!!session);
      });
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth guard state changed:', event, !!session);
      setIsAuthenticated(!!session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (isAuthenticated === null) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
};

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <div className="flex flex-col min-h-screen">
        <Toaster position="top-right" />
        {!window.location.pathname.startsWith('/admin') && <Navbar />}
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/apply" element={<ApplicationForm />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/why-us" element={<WhyUs />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/fraud-prevention" element={<FraudPrevention />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/personal-loans" element={<PersonalLoans />} />
          <Route path="/installment-loans" element={<InstallmentLoans />} />
          <Route path="/business-loans" element={<BusinessLoans />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/do-not-sell" element={<DoNotSell />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AuthGuard><AdminLogin /></AuthGuard>} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
          <Route path="/admin/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          
          {/* Catch all route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        {!window.location.pathname.startsWith('/admin') && <Footer />}
      </div>
    </Router>
  );
}

export default App;